﻿import { Directive } from "@app/components/directive";
import { NgComponent } from "@app/di/decorators";
import { Token } from "@app/di/token";
import { Dirs } from "@app/helpers/dirs";
import { TouchUtils, Utils } from "@tools";
import * as angular from "angular";

export interface ISortableScope extends ng.IScope {
    ngModel: Array<any>;
    itemSelector: string;
    handleSelector: string;
    horizontal: boolean;
    disabled: boolean;
    onStop: () => void;
}

@NgComponent({
    token: Token.KbSortable,
    dependencies: [
        Token.$Timeout
    ]
})
export class KbSortable extends Directive {
    constructor(
        $timeout: ng.ITimeoutService
    ) {
        super();

        this.restrict = "E";
        this.replace = true;
        this.transclude = true;
        this.require = "?ngModel";
        this.scope = {
            ngModel: "=",
            itemSelector: "@", // selector for the sortable items (point to the ng-repeat element)
            handleSelector: "@", // selector for the item's handle
            horizontal: "=?", // determines the orientation of the list
            disabled: "=?",
            onStop: "&", // callback after sorting (fires even if no change happened)
        };
        this.template = Dirs.component("kb-sortable");

        this.link = (scope: ISortableScope, elem: ng.IAugmentedJQuery, atts: ng.IAttributes, ngModel: ng.INgModelController) => {
            let itemQuery = "> *";
            let items: JQuery[] = [];
            let itemCaptured = false;
            let itemMoved = false;
            let $placeholder: JQuery = null;
            let $originalListDOM: JQuery<any> = null;
            let $currentItem: JQuery = null;
            let currentItemOriginalIndex: number = null;
            let currentItemNewIndex: number = null;
            let currentItemOriginalCSS = {
                width: "",
                height: "",
                top: "",
                left: "",
                position: "",
                zIndex: ""
            };
            let $itemScrollParent: JQuery = null;
            let $itemParent: JQuery = null;
            let offsetClick: JQuery.Coordinates = {
                top: 0,
                left: 0,
            };
            let offsetParent: JQuery.Coordinates = {
                top: 0,
                left: 0,
            };
            let itemPosition: JQuery.Coordinates = {
                top: 0,
                left: 0,
            };
            let itemPointerPosition: JQuery.Coordinates = {
                top: 0,
                left: 0,
            };
            let itemLastPointerPosition: JQuery.Coordinates = {
                top: 0,
                left: 0,
            };

            let setup = () => {
                itemQuery = "> *";
                if (scope.itemSelector) {
                    itemQuery = scope.itemSelector;
                }

                cleanDOM();

                if (!scope.disabled) {
                    setItems();
                    setHandles();
                }
            }

            let setItems = () => {
                items = [];

                elem.find(itemQuery).each((i, child) => {
                    items.push($(child));
                });
            }

            let setHandles = () => {
                // add handle per item
                $.each(items, (i, $item) => {
                    if (scope.handleSelector) {
                        $item.find(scope.handleSelector).addClass("kb-sortable-handle");
                    } else {
                        // if no handle selector given, then make the whole item the handle
                        $item.addClass("kb-sortable-handle");
                    }
                });
            }

            let mouseInit = () => {
                elem.on("mousedown", mouseDown);
                TouchUtils.addMouseSimulation(elem);
            }

            let mouseDown = (event: JQuery.MouseDownEvent) => {
                if (scope.disabled) {
                    return true;
                }

                let leftMouseBtn = (event.button === 0);
                if (!leftMouseBtn) {
                    return true;
                }

                // make sure items are set
                setItems();

                let itemClicked = null;
                if (items.length > 0) {
                    // get the clicked item
                    itemClicked = items.find(el => el[0] == event.target);

                    // item not found, search for a parent
                    if (!itemClicked) {
                        $(event.target).parents().each((i, parent) => {
                            itemClicked = items.find(el => el[0] == parent);
                            if (itemClicked) {
                                return false;
                            }
                        });
                    }
                }

                if (!itemClicked) {
                    return true;
                }

                if (scope.handleSelector) {
                    let validHandle = false;

                    $(scope.handleSelector, itemClicked).find("*").addBack().each((i, el) => {
                        if (el === event.target) {
                            validHandle = true;
                            return false;
                        }
                    });

                    // handle not found, abort sorting
                    if (!validHandle) {
                        return true;
                    }
                }

                cleanCurrentState();

                $currentItem = itemClicked;
                currentItemOriginalIndex = items.indexOf($currentItem);

                if (!itemCaptured) {
                    captureItem(event.pageY, event.pageX);
                }

                $(document).on('mousemove', mouseMove);
                $(document).on('mouseup', mouseUp);

                event.preventDefault();
                return true;
            }

            let mouseUp = () => {
                $(document).off('mousemove', mouseMove);
                $(document).off('mouseup', mouseUp);

                // somehow the placeholder is missing, abort sorting
                if (!$placeholder) {
                    restoreDOM();
                    return;
                }

                // update the DOM position of the item
                // only do this if we actually moved, otherwise the DOM update will cause inside clicks to be ignored
                if (itemMoved && $currentItem.parent().length) {
                    $placeholder.before($currentItem);
                }

                // remove the placeholder from the DOM
                $placeholder.remove();

                // restore the original css of the item
                for (let prop in currentItemOriginalCSS) {
                    // avoid default values to be set as a style attr
                    if (currentItemOriginalCSS[prop] === "auto" || currentItemOriginalCSS[prop] === "static") {
                        currentItemOriginalCSS[prop] = "";
                    }
                }
                $currentItem.css(currentItemOriginalCSS);

                // get new index based on the DOM
                currentItemNewIndex = elem.find(itemQuery).index($currentItem);

                // we should only update if we actually moved, otherwise inside clicks will be ignored
                if (itemMoved) {
                    restoreDOM();

                    if (currentItemNewIndex != null && currentItemOriginalIndex != null && currentItemNewIndex != currentItemOriginalIndex) {
                        // $apply so that ng-repeat renders the new order
                        scope.$apply(() => {
                            ngModel.$modelValue.splice(currentItemNewIndex, 0, ngModel.$modelValue.splice(currentItemOriginalIndex, 1)[0]);
                        });
                    }

                    if (scope.onStop) {
                        // timeout so that it executes on the next $digest
                        $timeout(() => {
                            scope.onStop();
                        });
                    }
                }

                cleanCurrentState();
            }

            let mouseMove = (event: JQuery.MouseMoveEvent) => {
                // the mouse has moved, we are committed to sorting even if positions don't change
                itemMoved = true;

                if (itemCaptured) {
                    mouseDrag(event.pageY, event.pageX);
                } else {
                    captureItem(event.pageY, event.pageX);
                    if (itemCaptured) {
                        mouseDrag(event.pageY, event.pageX);
                    } else {
                        // something happened, abort sorting
                        mouseUp();
                    }
                }

                return !itemCaptured;
            }

            let captureItem = (mouseY: number, mouseX: number) => {
                // save the original list DOM
                $originalListDOM = $currentItem.parent().contents();

                // don't inlcude the placeholder
                if ($placeholder) {
                    $originalListDOM = $originalListDOM.not($placeholder);
                }

                // save the item's original css
                currentItemOriginalCSS = {
                    width: $currentItem[0].style.width,
                    height: $currentItem[0].style.height,
                    top: $currentItem.css("top"),
                    left: $currentItem.css("left"),
                    position: $currentItem.css("position"),
                    zIndex: $currentItem.css("zIndex"),
                };

                // add the computed size if missing, will avoid a size change when changing the position
                if (!$currentItem[0].style.width) {
                    $currentItem.width($currentItem.width());
                }

                if (!$currentItem[0].style.height) {
                    $currentItem.height($currentItem.height());
                }

                let itemPosition = $currentItem.offset();

                // item's absolute position minus margins
                let offsetItem = {
                    top: itemPosition.top - parseCSSNumber($currentItem, "marginTop"),
                    left: itemPosition.left - parseCSSNumber($currentItem, "marginLeft")
                };

                // click position relative to the item
                offsetClick = {
                    top: mouseY - offsetItem.top,
                    left: mouseX - offsetItem.left
                };

                $itemScrollParent = Utils.getScrollParent($currentItem);
                $itemParent = $currentItem.offsetParent();

                let parentPosition = $itemParent.offset();

                if ($.contains($itemScrollParent[0], $itemParent[0])) {
                    // if the parent is inside a scroll it will have it's position offset so we need to restore it
                    parentPosition.top += $itemScrollParent.scrollTop();
                    parentPosition.left += $itemScrollParent.scrollLeft();
                }

                // item parent's absolute position plus borders
                offsetParent = {
                    top: parentPosition.top + parseCSSNumber($itemParent, "borderTopWidth"),
                    left: parentPosition.left + parseCSSNumber($itemParent, "borderLeftWidth")
                };

                // create the placeholder that will show the new position when moving the item
                createPlaceholder();

                // after calculating the offset and placeholder we can change the position
                $currentItem.css("position", "absolute");
                $currentItem.css("zIndex", 1000);

                itemCaptured = true;

                // execute the drag once to set the item's correct screen position
                mouseDrag(mouseY, mouseX);
            }

            let mouseDrag = (mouseY: number, mouseX: number) => {
                let scrollAmount = 20;

                // get the position for intersection check
                itemPointerPosition = {
                    top: mouseY - offsetClick.top,
                    left: mouseX - offsetClick.left
                };

                if (!itemLastPointerPosition) {
                    itemLastPointerPosition = itemPointerPosition;
                }

                // get the node that will act as the scroll offset for the item's screen position
                let scrollNode = $.contains($itemScrollParent[0], $itemParent[0]) ? $itemScrollParent : $itemParent;

                // get the screen position for the "flying" item
                itemPosition = {
                    top: itemPointerPosition.top - offsetParent.top + scrollNode.scrollTop(),
                    left: itemPointerPosition.left - offsetParent.left + scrollNode.scrollLeft()
                };

                let scrollParentPosition = $itemScrollParent.offset();

                // perform scrolling
                if ((scrollParentPosition.top + $itemScrollParent[0].offsetHeight - mouseY) < scrollAmount) {
                    $itemScrollParent[0].scrollTop += scrollAmount;
                    itemPointerPosition.top -= scrollAmount
                } else if ((mouseY - scrollParentPosition.top) < scrollAmount) {
                    $itemScrollParent[0].scrollTop -= scrollAmount;
                    itemPointerPosition.top += scrollAmount
                }

                if ((scrollParentPosition.left + $itemScrollParent[0].offsetWidth - mouseX) < scrollAmount) {
                    $itemScrollParent[0].scrollLeft += scrollAmount;
                    itemPointerPosition.left -= scrollAmount
                } else if ((mouseX - scrollParentPosition.left) < scrollAmount) {
                    $itemScrollParent[0].scrollLeft -= scrollAmount;
                    itemPointerPosition.left += scrollAmount
                }

                // set the position for the "flying" item
                $currentItem[0].style.top = itemPosition.top + "px";
                $currentItem[0].style.left = itemPosition.left + "px";

                // check intersection of items
                for (let i = items.length - 1; i >= 0; i--) {
                    let $item = items[i];

                    let intersection = intersectsWithPointer($item, scope.horizontal ? "left" : "top");
                    if (intersection == 0) {
                        continue;
                    }

                    // item can't intersect with itself
                    if ($item[0] !== $currentItem[0]) {
                        let movePrev = intersection == -1;

                        // check if the item captured is not the parent of the $item
                        if ($placeholder[movePrev ? "next" : "prev"]()[0] !== $item[0] && !$.contains($placeholder[0], $item[0])) {
                            // sort DOM
                            $item[0].parentNode.insertBefore($placeholder[0], (movePrev ? $item[0] : $item[0].nextSibling));
                            break;
                        }
                    }
                }

                itemLastPointerPosition = itemPointerPosition;
            }

            // 0: no intersection, 1: should move next, -1: should move prev
            let intersectsWithPointer = ($item: JQuery, dir: "top" | "left"): number => {
                let itemPosition = $item.offset();
                let itemClick = itemPointerPosition[dir] + offsetClick[dir];
                let itemSize = itemPosition[dir] + (dir == "top" ? $item.outerHeight() : $item.outerWidth());
                let isOver = (itemClick >= itemPosition[dir]) && (itemClick < itemSize);

                if (!isOver) {
                    return 0;
                }

                let delta = itemPointerPosition[dir] - itemLastPointerPosition[dir];

                if (delta == 0) {
                    return 0;
                }

                return delta > 0 ? 1 : -1;
            }

            let createPlaceholder = () => {
                let nodeName = $currentItem[0].nodeName.toLowerCase();

                $placeholder = $("<" + nodeName + ">", document);
                $placeholder.addClass($currentItem[0].className);
                $placeholder.css("visibility", "hidden");

                // append the placeholder after the actual item
                $currentItem.after($placeholder);

                // if the placeholder doesn't have a size we copy the computed size from the item
                if (!$placeholder.height()) {
                    $placeholder.outerHeight($currentItem.outerHeight());
                }

                if (!$placeholder.width()) {
                    $placeholder.outerWidth($currentItem.outerWidth());
                }
            }

            let restoreDOM = () => {
                if ($currentItem && $originalListDOM) {
                    if (!angular.equals($currentItem.parent().contents().toArray(), $originalListDOM.toArray())) {
                        // restore the original list DOM to preserve the ng-repeat scope
                        $originalListDOM.appendTo($currentItem.parent());
                    }
                }
            }

            let cleanCurrentState = () => {
                itemCaptured = false;
                itemMoved = false;
                $placeholder = null;
                $originalListDOM = null;
                $currentItem = null;
                currentItemOriginalIndex = null;
                currentItemNewIndex = null;
                currentItemOriginalCSS = {
                    width: "",
                    height: "",
                    top: "",
                    left: "",
                    position: "",
                    zIndex: ""
                };
                $itemScrollParent = null;
                $itemParent = null;
                offsetClick = {
                    top: 0,
                    left: 0,
                };
                offsetParent = {
                    top: 0,
                    left: 0,
                };
                itemPosition = {
                    top: 0,
                    left: 0,
                };
                itemPointerPosition = {
                    top: 0,
                    left: 0,
                };
                itemLastPointerPosition = {
                    top: 0,
                    left: 0,
                };
            }

            let cleanDOM = () => {
                // remove existing handles
                elem.find(".kb-sortable-handle").removeClass("kb-sortable-handle");

                // remove existing placeholder
                if ($placeholder) {
                    $placeholder.remove();
                }
            }

            let parseCSSNumber = ($item: JQuery, prop: string): number => {
                return parseInt($item.css(prop), 10) || 0;
            }

            setup();
            mouseInit();

            scope.$watchCollection('ngModel', () => {
                // timeout to let ng-repeat modify the DOM
                $timeout(() => {
                    setup();
                });
            });

            scope.$watch("handles", () => {
                setup();
            });

            scope.$watch("disabled", () => {
                setup();
            });

            scope.$on("$destroy", () => {
                cleanDOM()
                elem.off("mousedown", mouseDown);
                $(document).off('mousemove', mouseMove);
                $(document).off('mouseup', mouseUp);
                TouchUtils.removeMouseSimulation(elem);
            });
        };
    }
}